import "./Transporters.scss";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TransporterService from "../../services/TransporterService";
import { useLoader } from "../../contexts/LoaderContext";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent from "../../components/TableComponent/TableComponent";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import { SelectChangeEvent } from "@mui/material";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { useAuth } from "../../contexts/AuthContext";
import TransporterModal from "../../components/Transporter/TransporterModal";
import TransporterRegistrationModal from "../../components/Transporter/TransporterRegistrationModal";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { Transporter } from "../../interfaces/transporter.interface";

interface TransporterStats {
  totalTransporters: number;
}

function Transporters() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { userProfile } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [transporters, setTransporters] = useState<Transporter[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [transportersLoaded, setTransportersLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [transporterStats, setTransporterStats] = useState<TransporterStats>({
    totalTransporters: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [selectedTransporter, setSelectedTransporter] =
    useState<Transporter | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transporterToDelete, setTransporterToDelete] =
    useState<Transporter | null>(null);

  const fetchTransporters = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      try {
        if (!initialLoadDone.current) {
          setTransportersLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        let response;

        // Use role-based routing
        response = await TransporterService.getTransporters(userProfile);

        if (!response.data.error) {
          const transporters: Transporter[] = response.data.data;
          setTransporters(transporters);

          // Calculate stats
          const stats: TransporterStats = {
            totalTransporters: transporters.length,
          };
          setTransporterStats(stats);
        } else {
          console.error(
            "Error in fetching transporters:",
            response.data.message
          );
          setError(response.data.message || "Error fetching transporters");
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setTransportersLoaded(true);
        initialLoadDone.current = true;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [showLoader, hideLoader, loader, userProfile]
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    return transporters.map((transporter) => ({
      id: transporter.id,
      name: transporter.name,
      uid: transporter.uid,
      contact: transporter.phoneNumber || transporter.contact || "N/A",
      email: transporter.email || "N/A",
      userCount: transporter.users?.length || 0,
      createdBy: transporter.createdBy?.name || "N/A",
      createDate: transporter.createDate
        ? new Date(transporter.createDate).toLocaleDateString()
        : "N/A",
      // Keep legacy address for backward compatibility
      address: transporter.address || "N/A",
    }));
  }, [transporters]);

  // Memoize filtered and paginated data
  const filteredAndPaginatedData = useMemo(() => {
    let filteredData = tableData;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter((row) =>
        Object.values(row).some((value) =>
          value?.toString().toLowerCase().includes(query)
        )
      );
    }

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: filteredData.slice(startIndex, endIndex),
      totalElements: filteredData.length,
      totalPages: Math.ceil(filteredData.length / pageSize),
    };
  }, [tableData, searchQuery, currentPage, pageSize]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        if (row.name) suggestions.add(row.name);
        if (row.uid) suggestions.add(row.uid);
        if (row.contact && row.contact !== "N/A") suggestions.add(row.contact);
        if (row.email && row.email !== "N/A") suggestions.add(row.email);
        if (row.createdBy && row.createdBy !== "N/A")
          suggestions.add(row.createdBy);
        if (row.address && row.address !== "N/A") suggestions.add(row.address);
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Memoize columns definition
  const columns = useMemo(
    () => [
      {
        header: "Transporter Name",
        accessor: "name",
        sortable: true,
      },
      {
        header: "Contact Number",
        accessor: "contact",
        sortable: true,
      },
      {
        header: "Email",
        accessor: "email",
        sortable: true,
      },
      // {
      //   header: "Users",
      //   accessor: "userCount",
      //   sortable: true,
      // },
      {
        header: "UID",
        accessor: "uid",
        sortable: true,
      },
      {
        header: "Created By",
        accessor: "createdBy",
        sortable: true,
      },
      {
        header: "Created Date",
        accessor: "createDate",
        sortable: true,
      },
    ],
    []
  );

  // Handle edit transporter
  const handleEditTransporter = useCallback((transporter: Transporter) => {
    setSelectedTransporter(transporter);
    setIsModalOpen(true);
  }, []);

  // Handle delete transporter
  const handleDeleteTransporter = useCallback(
    async (transporterId: number) => {
      const transporter = transporters.find((t) => t.id === transporterId);
      if (transporter) {
        setTransporterToDelete(transporter);
        setDeleteDialogOpen(true);
      }
    },
    [transporters]
  );

  // Memoize actions for the table
  const tableActions = useMemo(
    () => [
      {
        label: "Edit",
        callback: (row: Record<string, any>) => {
          const transporter = transporters.find((t) => t.id === row.id);
          if (transporter) {
            handleEditTransporter(transporter);
          }
        },
        isEnabled: () => true,
      },
      {
        label: "Delete",
        callback: (row: Record<string, any>) => {
          handleDeleteTransporter(row.id);
        },
        isEnabled: () => true,
      },
    ],
    [transporters, handleEditTransporter, handleDeleteTransporter]
  );

  // Memoize stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Transporters",
        value: transporterStats.totalTransporters,
        valueColor: "#59585B",
      },
    ],
    [transporterStats]
  );

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchTransporters(false);
      } finally {
        // No need to hide loader here as it's handled in fetchTransporters
      }
    };

    fetchData();
  }, [fetchTransporters]);

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    fetchTransporters(true, true);
  }, [fetchTransporters]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1); // Reset to first page when changing page size
    },
    []
  );

  // Handle add transporter
  const handleAddTransporter = useCallback(() => {
    setIsRegistrationModalOpen(true);
  }, []);

  // Handle modal success
  const handleModalSuccess = useCallback(() => {
    fetchTransporters(true, true);
  }, [fetchTransporters]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTransporter(null);
  }, []);

  // Handle registration modal close
  const handleRegistrationModalClose = useCallback(() => {
    setIsRegistrationModalOpen(false);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!transporterToDelete) return;

    try {
      showLoader();

      // Check user role to determine which API to call
      if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
        // Enterprise admin - use enterprise transporters endpoint
        await TransporterService.deleteTransporter(transporterToDelete.id);
      } else {
        // Federation admin - use federation transporters endpoint
        await TransporterService.deleteFederationTransporter(
          transporterToDelete.id
        );
      }

      showToast("Transporter deleted successfully", "success");
      // Refresh the list
      fetchTransporters(true, true);
    } catch (error) {
      console.error("Error deleting transporter:", error);
      showToast("Error deleting transporter", "error");
    } finally {
      hideLoader();
      setDeleteDialogOpen(false);
      setTransporterToDelete(null);
    }
  }, [
    transporterToDelete,
    fetchTransporters,
    showLoader,
    hideLoader,
    userProfile,
  ]);

  // Handle delete dialog close
  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setTransporterToDelete(null);
  }, []);

  return (
    <div className="transporters-page p-[20px]">
      <StatsBoxContainer stats={statsData} loading={!transportersLoaded} />

      <div className="flex justify-between items-center py-[30px]">
        <SearchBar
          placeholder="Search transporters..."
          onSearch={handleSearchChange}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            setSearchQuery(suggestion);
            setCurrentPage(1); // Reset to first page on suggestion click
          }}
        />
        <div className="flex items-center space-x-2">
          <button
            type="button"
            className="primary_button"
            onClick={handleAddTransporter}
          >
            Add Transporter
          </button>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      {transportersLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No transporters found</p>
        </div>
      ) : (
        <div className="table">
          <TableComponent
            showPagination={true}
            columns={columns}
            data={tableData}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="id"
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(tableData.length / pageSize)}
            totalElements={tableData.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableClientSideFiltering={true}
            actions={tableActions}
          />
        </div>
      )}

      {/* Transporter Modal */}
      <TransporterModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        transporter={selectedTransporter}
        onSuccess={handleModalSuccess}
      />

      {/* Transporter Registration Modal */}
      <TransporterRegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={handleRegistrationModalClose}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Transporter"
        message={`Are you sure you want to delete the transporter "${transporterToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </div>
  );
}

export default Transporters;
